import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { trackEvent } from '../lib/analytics';

// System deck constants
export const SYSTEM_COMMENTS_DECK_NAME = 'Comments';

// Decks store with Supabase sync (local-first)
// NOTE: We explicitly filter out deleted records at query level to prevent ghost decks
// This works together with fieldDeleted in defaultSyncConfig for proper soft delete handling
export const decks$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_decks',
  // Explicitly exclude deleted records - belt-and-suspenders with fieldDeleted
  filter: (select) => select.or('deleted.is.null,deleted.eq.false'),
  create: createSyncCreate('reedle_decks'),
  update: createSyncUpdate('reedle_decks'),
  ...defaultSyncConfig,
  persist: {
    name: 'reedle-decks',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

/**
 * Get decks as array (system deck first, then default deck, then sorted by created_at)
 */
export const getDecksArray = () => {
  const decksObj = decks$.get() || {};
  return Object.values(decksObj)
    .filter(deck => deck && deck.id && !deck.deleted)
    .sort((a, b) => {
      // System decks are fixed utility destinations, like Inbox for article lists.
      if (a.is_system && !b.is_system) return -1;
      if (!a.is_system && b.is_system) return 1;
      // Default deck follows the system deck.
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      // Then by created_at
      return new Date(a.created_at) - new Date(b.created_at);
    });
};

/**
 * Check if a deck is a system deck (non-editable/deletable)
 */
export const isSystemDeck = (deckId) => {
  const deck = decks$[deckId]?.get();
  return deck?.is_system === true;
};

/**
 * Get deck by ID
 */
export const getDeckById = (deckId) => {
  return decks$[deckId].get();
};

/**
 * Create a new deck
 */
export const createDeck = async (name = 'Default Deck') => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  const id = uuidv4();
  const now = new Date().toISOString();

  const newDeck = {
    id,
    user_id: userId,
    name: name.trim(),
    new_cards_per_day: 20,
    review_cards_per_day: 200,
    created_at: now,
    updated_at: now,
  };

  decks$[id].set(newDeck);

  trackEvent('deck_created', {
    deck_id: id,
    deck_name: newDeck.name,
    is_system_deck: false,
  });

  return newDeck;
};

/**
 * Update deck settings
 */
export const updateDeck = (deckId, updates) => {
  decks$[deckId].assign({
    ...updates,
    updated_at: new Date().toISOString(),
  });
};

/**
 * Rename a deck
 */
export const renameDeck = (deckId, newName) => {
  updateDeck(deckId, { name: newName.trim() });
};

/**
 * Delete a deck (cascade deletes cards)
 * Cannot delete the default deck or system decks
 */
export const deleteDeck = (deckId) => {
  const deck = decks$[deckId].get();
  if (deck?.is_default) {
    console.warn('Cannot delete default deck');
    return false;
  }
  if (deck?.is_system) {
    console.warn('Cannot delete system deck');
    return false;
  }
  decks$[deckId].delete();
  return true;
};

/**
 * Get the default deck for the current user
 */
export const getDefaultDeck = () => {
  const decksObj = decks$.get() || {};
  return Object.values(decksObj).find(deck => deck && deck.is_default && !deck.deleted);
};

/**
 * Get the system Comments deck for the current user
 */
export const getSystemCommentsDeck = () => {
  const decksObj = decks$.get() || {};
  return Object.values(decksObj).find(
    deck => deck && deck.is_system && deck.name === SYSTEM_COMMENTS_DECK_NAME && !deck.deleted
  );
};

/**
 * Get the system Comments deck, with DB fallback for hydration lag.
 * Primary mechanism: DB trigger creates the deck at user signup.
 * Fallback: query Supabase directly if not yet in local state (e.g. first session).
 * Last resort: create locally (idempotent — UNIQUE INDEX prevents DB duplicates).
 */
export const getOrCreateSystemDeck = async () => {
  const userId = auth$.user.get()?.id;
  if (!userId) throw new Error('User not authenticated');

  // 1. Check local observable state (fast path)
  let systemDeck = getSystemCommentsDeck();
  if (systemDeck) return systemDeck;

  // 2. DB fallback: handles hydration lag on first session
  try {
    const { data } = await supabase
      .from('reedle_decks')
      .select('*')
      .eq('user_id', userId)
      .eq('is_system', true)
      .or('deleted.is.null,deleted.eq.false')
      .limit(1)
      .single();
    if (data) {
      decks$[data.id].set(data);
      return data;
    }
  } catch {
    // Not found in DB — fall through to creation
  }

  // 3. Last resort: create locally (DB UNIQUE INDEX prevents duplicates)
  // This should only happen for old users before backfill, or in rare race conditions.
  const id = uuidv4();
  const now = new Date().toISOString();
  const newDeck = {
    id,
    user_id: userId,
    name: SYSTEM_COMMENTS_DECK_NAME,
    is_system: true,
    new_cards_per_day: 20,
    review_cards_per_day: 200,
    created_at: now,
    updated_at: now,
  };
  decks$[id].set(newDeck);
  return newDeck;
};
